import dayjs from "dayjs";
import router from "../../router";
import i18n from "../i18n";
import services from "../services";
import store from "../store";

const strict = true;

const state = {
  userData: {},
  domicilioCities: [],
  residenzaCities: [],
  lavoroCities: [],
  domicilioForeignCities: [],
  residenzaForeignCities: [],
  lavoroForeignCities: [],
  allProvinces: [],
  countriesForIban: [],
  nucleoFamiliareFull: [],
  isConiuge: false
};

const getters = {
  userData: state => state.userData,
  domicilioCities: state => state.domicilioCities,
  residenzaCities: state => state.residenzaCities,
  lavoroCities: state => state.lavoroCities,
  domicilioForeignCities: state => state.domicilioForeignCities,
  residenzaForeignCities: state => state.residenzaForeignCities,
  lavoroForeignCities: state => state.lavoroForeignCities,
  allProvinces: state => state.allProvinces,
  getCountriesForIban: state => state.countriesForIban,
  getNucleoFamiliareFull: state => state.nucleoFamiliareFull,
  getIsConiuge: state => state.isConiuge
};

const mutations = {
  reset(state) {
    state.userData = {};
    state.domicilioCities = [];
    state.residenzaCities = [];
    state.lavoroCities = [];
    state.domicilioForeignCities = [];
    state.residenzaForeignCities = [];
    state.lavoroForeignCities = [];
    state.allProvinces = [];
    state.countriesForIban = [];
    state.nucleoFamiliareFull = [];
    state.isConiuge = false;
  },
  updateUserData(state, fields) {
    state.userData = { ...state.userData, ...fields };
  },
  domicilioCities(state, cities) {
    state.domicilioCities = cities;
  },
  residenzaCities(state, cities) {
    state.residenzaCities = cities;
  },
  lavoroCities(state, cities) {
    state.lavoroCities = cities;
  },
  domicilioForeignCities(state, cities) {
    state.domicilioForeignCities = cities;
  },
  residenzaForeignCities(state, cities) {
    state.residenzaForeignCities = cities;
  },
  lavoroForeignCities(state, cities) {
    state.lavoroForeignCities = cities;
  },
  provinces(state, provinces) {
    state.allProvinces = provinces;
  },
  setCountriesForIban(state, countries) {
    // remove countries that have "cifreIban" property equal to null
    let validCountries = countries.filter(c => {
      return c.cifreIban != null;
    });
    // sort countries using "descrizione" property
    validCountries.sort((a, b) =>
      a?.descrizione?.toUpperCase() > b?.descrizione?.toUpperCase() ? 1 : -1
    );
    state.countriesForIban = validCountries;
  },
  setNucleoFamiliareFull(state, result) {
    state.nucleoFamiliareFull = result;
  },
  setIsConiuge(state, { monthsForNucleoFamiliareFull, idIscritto }) {
    let isConiugeValidValues = [
      "Coniuge (matrimonio o unione civile)".toUpperCase(),
      "Convivente di fatto (L. 76/2016)".toUpperCase()
    ];
    let isConiuge = state.nucleoFamiliareFull.find(
      n =>
        isConiugeValidValues.includes(n?.gradoParentela.toUpperCase()) &&
        idIscritto === n.idIscritto
    );
    if (!isConiuge) {
      state.isConiuge = false;
    } else {
      let hasAlmostFE1Covered = monthsForNucleoFamiliareFull.some(
        m => m.covered && m.codicePacchetto.includes("FE1")
      );
      if (hasAlmostFE1Covered) {
        state.isConiuge = false;
      } else {
        state.isConiuge = true;
      }
    }
  }
  // setTrueToSimulateMemberAsConiuge(state) {
  //   state.isConiuge = true;
  // }
};

const actions = {
  /**
   * dispatch to be used whenever a member's property is changed:
   *
   * 1. update profile userData property using "updateUserData" mutation
   * 2. update login userInfo property using "login/updateUserInfo" mutation
   * 3. recalculate profile completion percentage using "login/calculateCompletion" mutation
   */
  async updateUserData({ commit }, fields) {
    await commit("updateUserData", fields);
    await commit("login/updateUserInfo", fields, { root: true });
    await commit("login/calculateCompletion", null, { root: true });
  },
  async initUserAddressesData({ commit, dispatch, getters, rootGetters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let user = { ...rootGetters["login/userInfo"] };
      let provinces = await services.profileService.getAllProvinces();
      commit("provinces", provinces);
      let coveredAndBookableProvinces = provinces.filter(
        p => p.covered && p.bookable
      );
      let addressTypes = ["domicilio", "residenza"];
      for (let type of addressTypes) {
        let isEstero = user[`${type}IsForeign`];
        let cityId = user[`${type}ComuneId`];
        let cityName = user[`${type}ComuneDescrizione`];
        if (cityId && cityName) {
          await dispatch("getCitiesForAutocomplete", {
            type: type,
            search: cityName,
            size: 20,
            estero: isEstero
          });
          let cities = isEstero
            ? getters[`${type}ForeignCities`]
            : getters[`${type}Cities`];
          let city = null;
          if (cities && cities.length) {
            city = cities.find(c => {
              return isEstero
                ? c?.idComune === cityId || null
                : c?.city?.id === cityId || null;
            });
            city = city || cities[0];
            let province = isEstero
              ? null
              : coveredAndBookableProvinces.find(
                  ({ code }) => code === city.province.code
                );
            Object.assign(user, {
              [`${type}ProvinceCode`]: isEstero ? "EE" : city?.province?.code,
              [`${type}Province`]: isEstero
                ? "ESTERO"
                : city?.province?.description,
              [`${type}ProvinceCovered`]: isEstero
                ? false
                : province
                ? true
                : false,
              [`${type}ProvinceBookable`]: isEstero
                ? false
                : province
                ? true
                : false
            });
          }
          commit(`${type}Cities`, [city]);
        }
      }
      dispatch("updateUserData", user);
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async callUpdateUserInfo({ rootGetters, commit, dispatch }, data) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let userData = { ...rootGetters["login/userInfo"] };
      let keys = Object.keys(data);
      for (let key of keys) {
        userData[key] = data[key];
      }
      await services.profileService.updateUserInfo(userData);
      await dispatch("updateUserData", data);
      return;
    } catch (error) {
      commit("error/setError", error, {
        root: true
      });
      return;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async cancelGeoreference({ commit, dispatch }, type) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let geoInfo = {
        geo: "",
        geoAutorizzata: false
      };
      if (type === "domicilio") {
        await dispatch("updateHomeAddress", {
          ...geoInfo
        });
      } else if (type === "lavoro") {
        await dispatch("updateWorkAddress", {
          ...geoInfo
        });
      } else {
        await dispatch("updateResidenceAddress", {
          ...geoInfo
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async changePreferredContactChannel({ commit, dispatch }, channel) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let fun =
        channel === "E" ? "setContactChannelEmail" : "setContactChannelPhone";
      await services.profileService[fun]();
      dispatch("updateUserData", {
        preferenzaContatto: channel
      });
      return;
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async changeSendVoucherMode({ commit, dispatch }, channel) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      let payload = {};
      payload["modalitaInvioVoucher"] = channel;
      await services.profileService.changeSendVoucherMode(channel);
      await dispatch("updateUserData", payload);
      return;
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async concludePrimoAccesso({ commit, dispatch }, healthInsuranceText) {
    try {
      commit("loading/openLoadingDialog", null, {
        root: true
      });
      // we need to call the updateUserInfo service to update "primoAccesso" and "dataPrimoAccesso" properites
      let payload = {};
      payload["primoAccesso"] = true;
      payload["dataPrimoAccesso"] = dayjs().format();
      await dispatch("callUpdateUserInfo", payload);
      // open success dialog
      let title = i18n.t("profile.address.dialog.update.success.titlePA");
      let text = i18n.t("profile.address.dialog.update.success.textPA", {
        healthInsurance: healthInsuranceText
      });
      commit(
        "baseDialog/openDialog",
        {
          title: title,
          text: text,
          buttons: [
            {
              text: i18n.t("profile.address.dialog.update.success.btnLabel"),
              outlined: false,
              action: () => {
                commit("baseDialog/reset", null, {
                  root: true
                });
              }
            }
          ]
        },
        {
          root: true
        }
      );
      return;
    } catch (error) {
      console.error(error);
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getCitiesForAutocomplete({ commit }, payload) {
    try {
      let result = [];
      if (payload?.estero) {
        result = await services.profileService.callGetCountriesForAddress();
        commit(`${payload?.type}ForeignCities`, result);
      } else {
        result = await services.profileService.callGetCities(payload);
        commit(`${payload?.type}Cities`, result);
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async getCityIdForItalianAddress({ commit }, payload) {
    try {
      let search = payload["search"];
      let provCode = payload["provinceCode"];
      delete payload.provinceCode;
      let result = await services.profileService.callGetCities(payload);
      return findCity(result, search, provCode);
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async getCityIdForForeignAddress({ commit }, toSearch) {
    try {
      let result = await services.profileService.callGetCountriesForAddress();
      let found = result.find(c => {
        return c?.descrizione.includes(toSearch.toUpperCase()) || null;
      });
      return found?.idComune || null;
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async updateAddress({ commit, dispatch }, { type, addressInfo }) {
    commit("loading/openLoadingDialog", null, { root: true });
    let geoInfo = {
      indirizzo: addressInfo.address,
      idComune: addressInfo.city.id,
      localita: addressInfo.province.description,
      geo: addressInfo.geo || "",
      geoAutorizzata: Boolean(addressInfo.geo),
      description: addressInfo.city.description,
      CAP: addressInfo.city.cap,
      provinceCode: addressInfo.province.code,
      provinceCovered: addressInfo.province.covered,
      provinceBookable: addressInfo.province.bookable,
      needUpdateForPrimoAccesso: false,
      addressSavedForPrimoAccesso: true
    };
    if (type === "domicilio") {
      await dispatch("updateHomeAddress", geoInfo);
    } else if (type === "lavoro") {
      await dispatch("updateWorkAddress", geoInfo);
    } else {
      await dispatch("updateResidenceAddress", geoInfo);
    }
    commit("loading/reset", null, { root: true });
  },
  async updateHomeAddress({ commit, dispatch }, payload) {
    try {
      await services.profileService.updateHomeAddress(payload);
      dispatch("updateUserData", {
        domicilioIndirizzo: payload.indirizzo,
        domicilioComuneDescrizione: payload.description,
        domicilioComuneId: payload.idComune,
        domicilioCAP: payload.CAP,
        domicilioGeo: payload.geo,
        domicilioGeoAutorizzato: payload.geoAutorizzata,
        domicilioProvince: payload.localita,
        domicilioProvinceCovered: payload.provinceCovered,
        domicilioProvinceBookable: payload.provinceBookable,
        domicilioProvinceCode: payload.provinceCode,
        domicilioIsForeign:
          payload.provinceCode && payload.provinceCode === "EE",
        domicilioNeedUpdateForPrimoAccesso: payload.needUpdateForPrimoAccesso,
        domicilioAddressSavedForPrimoAccesso:
          payload.addressSavedForPrimoAccesso
      });
    } catch (error) {
      commit(
        "error/setError",
        "profile.address.dialog.update.error.generic.text",
        { root: true }
      );
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async updateResidenceAddress({ commit, dispatch }, payload) {
    try {
      await services.profileService.updateResidenceAddress(payload);
      dispatch("updateUserData", {
        residenzaIndirizzo: payload.indirizzo,
        residenzaComuneDescrizione: payload.description,
        residenzaComuneId: payload.idComune,
        residenzaCAP: payload.CAP,
        residenzaGeo: payload.geo,
        residenzaGeoAutorizzata: payload.geoAutorizzata,
        residenzaProvince: payload.localita,
        residenzaProvinceCovered: payload.provinceCovered,
        residenzaProvinceBookable: payload.provinceBookable,
        residenzaProvinceCode: payload.provinceCode,
        residenzaIsForeign:
          payload.provinceCode && payload.provinceCode === "EE",
        residenzaNeedUpdateForPrimoAccesso: payload.needUpdateForPrimoAccesso,
        residenzaAddressSavedForPrimoAccesso:
          payload.addressSavedForPrimoAccesso
      });
    } catch (error) {
      commit(
        "error/setError",
        "profile.address.dialog.update.error.generic.text",
        { root: true }
      );
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async updateWorkAddress({ dispatch }, payload) {
    try {
      await services.profileService.updateWorkAddress(payload);
      dispatch("updateUserData", {
        lavoroIndirizzo: payload.indirizzo,
        lavoroComuneDescrizione: payload.description,
        lavoroComuneId: payload.idComune,
        lavoroCAP: payload.CAP,
        lavoroGeo: payload.geo,
        lavoroGeoAutorizzata: payload.geoAutorizzata,
        lavoroProvince: payload.localita,
        lavoroProvinceCovered: payload.provinceCovered,
        lavoroProvinceBookable: payload.provinceBookable,
        lavoroProvinceCode: payload.provinceCode,
        lavoroIsForeign: payload.provinceCode && payload.provinceCode === "EE",
        lavoroNeedUpdateForPrimoAccesso: payload.needUpdateForPrimoAccesso,
        lavoroAddressSavedForPrimoAccesso: payload.addressSavedForPrimoAccesso
      });
    } catch (error) {
      console.error(error);
    }
  },
  async updateConsents({ dispatch, getters, commit }, payload) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let user = getters["userData"];
      await services.profileService.updateConsents(payload);
      dispatch("updateUserData", {
        ...payload
      });
      commit("login/setConsentToDeclare", false, { root: true });
      if (!user.primoAccesso || user.primoAccesso === null) {
        router.replace({ name: "profile" });
      } else {
        router.replace({ name: "dashboard" });
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async downloadInfoPDFFile({ commit }, documentType) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let docXML = await services.profileService.callDownloadInfoPDFFile({
        docType: documentType
      });
      let blob = new Blob([docXML.data], { type: "application/pdf" });
      let types = {
        PRESTAZIONIDIRETTE: "Informativa_prestazioni_dirette.pdf",
        PRESTAZIONIINDIRETTE: "Informativa_prestazioni_indirette.pdf",
        DATINAVIGAZIONE: "Informativa_dati_di_navigazione.pdf",
        UNIFICATA: "Informativa_prestazioni_indirette_e_dirette.pdf",
        CONDIZIONIUTILIZZO: "Condizioni_di_utilizzo.pdf"
      };
      let file = window.URL.createObjectURL(blob, {
        type: "application/pdf"
      });
      let a = document.createElement("a");
      a.href = file;
      a.download = types[documentType]
        ? types[documentType]
        : documentType + ".pdf";
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getPin({ commit }) {
    try {
      return await services.profileService.callGetMemberPin();
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async regeneratePin({ commit, dispatch }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      await services.profileService.callRegeneratePin();
      let memberPin = await dispatch("getPin");
      await dispatch("updateUserData", { pin: memberPin });
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async saveIban({ commit, dispatch }, payload) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      await services.profileService.updateIban({ iban: payload.value });
      await dispatch("updateUserData", {
        codiceIBAN: payload.value
      });
      commit(
        "baseDialog/openDialog",
        {
          title: i18n.t("profile.iban.successDialog.title"),
          text: payload.successMessage,
          buttons: [
            {
              text: i18n.t("ok"),
              outlined: false,
              action: () => {
                commit("baseDialog/reset", null, {
                  root: true
                });
              }
            }
          ]
        },
        { root: true }
      );
    } catch (error) {
      let errorDetails = {
        ...error
      };
      let excError = errorDetails?.response?.headers["x-onair-exception-type"];
      if (excError && excError === "nest.common.FondoestApiException") {
        errorDetails.response.headers["x-onair-exception-type"] =
          "nest.common.UpdateIbanException";
      }
      commit("error/setError", errorDetails, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getCountriesForIban({ commit }) {
    try {
      let ibanCountries =
        await services.profileService.callGetCountriesForIban();
      commit("setCountriesForIban", ibanCountries);
    } catch (error) {
      commit("error/setError", error, { root: true });
    }
  },
  async updateCertifiedContactsOnSiSalute({ commit }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      return await services.profileService.callUpdateCertifiedContactsOnSiSalute();
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getNucleoFamiliareFull({ commit, rootGetters }, memberId) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let result = await services.profileService.callGetNucleoFamiliareFull(
        memberId
      );
      commit("setNucleoFamiliareFull", result);
      let monthsForNucleoFamiliareFull =
        rootGetters["coverages/getCoverageMonthsForNucleoFamiliareFull"];
      let idIscritto = rootGetters["login/userInfo"]?.idIscritto;
      commit("setIsConiuge", {
        monthsForNucleoFamiliareFull,
        idIscritto
      });
      //
      //
      // ---> SETS TRUE TO SIMULATE MEMBER AS CONIUGE <---
      // commit("setTrueToSimulateMemberAsConiuge");
      //
      //
    } catch (error) {
      let errorDetails = {
        ...error
      };
      let excError = errorDetails?.response?.headers["x-onair-exception-type"];
      if (excError && excError === "nest.common.FondoestApiException") {
        errorDetails.response.headers["x-onair-exception-type"] =
          "nest.common.MemberNucleoFamiliareException";
      }
      commit("error/setError", errorDetails, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  }
};

const findCity = (result, search, provCode) => {
  if (!search || !result?.length) {
    return null;
  }
  const normalizedSearch = search.toUpperCase();
  const normalizedProvince = provCode?.toUpperCase();

  // 1. try using city name and province code, both with equals
  let found = result.find(r => {
    return (
      r?.city?.description?.toUpperCase() === normalizedSearch &&
      r?.province?.code?.trim().toUpperCase() === normalizedProvince
    );
  });

  // 2. try using only city name (equals)
  if (!found) {
    found = result.find(r => {
      return r?.city?.description?.toUpperCase() === normalizedSearch;
    });
  }

  // Ticket #28781 (Google returns GAZZO but we have GAZZO PADOVANO)
  // 3. try using city name (includes) and province code (equals)
  if (!found) {
    found = result.find(r => {
      return (
        r?.city?.description?.toUpperCase().includes(normalizedSearch) &&
        r?.province?.code?.toUpperCase() === normalizedProvince &&
        isCAPMatching(r)
      );
    });
  }

  // 4. should find a city to use, if not it is a missing city into JSON files
  return found?.city || null;
};

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
